/**
 * Issue #714: Diagnose why set_filters returned 0 results.
 * Compares applied filter values against actual inventory to find mismatches.
 *
 * Exported as a standalone pure function so it can be imported
 * both in CommandExecutor and in tests without duplication.
 *
 * @param {Object} filters - The filters that were applied
 * @param {Array} allCars - Full inventory array
 * @param {Array<string>} categoricalFilters - List of categorical filter keys
 * @param {Array<string>} rangeFilters - List of range filter keys
 * @returns {Array} Array of diagnosis objects, empty if no issues found
 */
export function diagnoseZeroResults(filters, allCars, categoricalFilters, rangeFilters) {
  if (!allCars || !Array.isArray(allCars) || allCars.length === 0) {
    return [];
  }

  const diagnosis = [];

  // Check each categorical filter value against FULL inventory (not faceted).
  // Validity check: "Mazda" exists in inventory? Yes → valid, regardless of model.
  // availableValues: scoped by other VALID categorical filters for better hints.
  const allValidValues = {};
  for (const filterType of categoricalFilters) {
    allValidValues[filterType] = [...new Set(allCars.map(c => c[filterType]).filter(Boolean))].sort();
  }

  for (const filterType of categoricalFilters) {
    const filterValues = filters[filterType];
    if (!Array.isArray(filterValues) || filterValues.length === 0) continue;

    for (const val of filterValues) {
      if (!allValidValues[filterType].includes(val)) {
        // For availableValues hint: scope by other VALID categorical filters
        // e.g. show models for Mazda (not all models) when make is valid
        let scopedCars = allCars;
        for (const otherFilter of categoricalFilters) {
          if (otherFilter === filterType) continue;
          const otherValues = filters[otherFilter];
          if (Array.isArray(otherValues) && otherValues.length > 0) {
            // Only scope by values that actually exist in inventory
            const validOtherValues = otherValues.filter(v => allValidValues[otherFilter].includes(v));
            if (validOtherValues.length > 0) {
              scopedCars = scopedCars.filter(c => validOtherValues.includes(c[otherFilter]));
            }
          }
        }
        const scopedValues = [...new Set(scopedCars.map(c => c[filterType]).filter(Boolean))].sort();

        diagnosis.push({
          filter: filterType,
          invalidValue: val,
          issue: 'VALUE_NOT_IN_INVENTORY',
          availableValues: scopedValues.slice(0, 30),
        });
      }
    }
  }

  // Check range filters against inventory bounds (faceted by categorical filters)
  for (const filterType of rangeFilters) {
    const range = filters[filterType];
    if (!range || typeof range !== 'object') continue;

    // Scope by other VALID categorical filters (consistent with categorical fix above)
    let relevantCars = allCars;
    for (const otherFilter of categoricalFilters) {
      const otherValues = filters[otherFilter];
      if (Array.isArray(otherValues) && otherValues.length > 0) {
        const validOtherValues = otherValues.filter(v => allValidValues[otherFilter].includes(v));
        if (validOtherValues.length > 0) {
          relevantCars = relevantCars.filter(c => validOtherValues.includes(c[otherFilter]));
        }
      }
    }

    const values = relevantCars.map(c => c[filterType]).filter(v => typeof v === 'number' && v > 0);
    if (values.length === 0) continue;

    const inventoryMin = Math.min(...values);
    const inventoryMax = Math.max(...values);

    if (
      (range.min !== undefined && range.min > inventoryMax) ||
      (range.max !== undefined && range.max < inventoryMin)
    ) {
      diagnosis.push({
        filter: filterType,
        requestedRange: { min: range.min, max: range.max },
        issue: 'RANGE_NO_OVERLAP',
        inventoryRange: { min: inventoryMin, max: inventoryMax },
      });
    }
  }

  // If all values are valid individually but combination yields 0 — flag it
  if (diagnosis.length === 0) {
    diagnosis.push({
      issue: 'COMBINATION_TOO_NARROW',
      hint: 'Each filter value exists in inventory, but the combination yields 0 results. Try broadening one or more filters.',
    });
  }

  return diagnosis;
}
